/**
 * The service's answer becomes an ordinary local project: parsed through the same steps as a project opened by its
 * link (the catalog is asked about its models first, since their tags, sizes and pivots decide how the doors and
 * windows stand), stored in this browser and set to open where the app would open it — in 3D, or on the plan when the
 * result is weak.
 */
import { parseProject } from '../core/hp/parseProject';
import type { Scene } from '../core/hp/types';
import { freshUniqueIds } from '../core/hp/uniqueIds';
import { ModelLibrary } from '../render/three/ModelLibrary';
import { writeProject } from '../state/projectLibrary';
import { parseProjectView, writeProjectView } from '../state/projectView';
import type { RecognizedProject } from './controller';
import { opensOnPlan, RecognitionError, type FloorStatus } from './protocol';
import { t } from '../i18n/I18n';

export async function sceneFromXml(xml: string, projectId: string, onProgress: (done: number, total: number) => void): Promise<Scene> {
  const library = new ModelLibrary();
  try {
    await library.manifest();
    const draft = parseProject(xml, { projectId });
    await library.prepare(draft.assets.models.map(model => model.id), onProgress);
    return parseProject(xml, { projectId, modelTags: library.tagMap(), catalogDims: library.dimsMap(), catalogPivots: library.pivotMap() });
  } finally {
    library.dispose();
  }
}

/** Names from a camera or a screenshot tool say nothing about the plan. */
// i18n-ignore: words matched in file names that cameras and screenshot tools give, not interface text
const MACHINE_NAME = /^(img|dsc|dscn|pxl|photo|image|scan|screenshot|screen shot|снимок экрана|скриншот|изображение|фото)(?=$|[\s\d_.-])/iu;

/** The typed name, else a file's own if it reads like one, else what the plans are. */
export function recognizedName(typed: string, files: readonly string[]): string {
  const name = typed.trim();
  if (name) return name.slice(0, 80);
  const base = (files[0] ?? '').replace(/\.[^.]+$/, '').replace(/[_]+/g, ' ').replace(/\s+/g, ' ').trim();
  if (base && !MACHINE_NAME.test(base) && /\p{L}{3}/u.test(base)) return base.slice(0, 80);
  return files.length > 1 ? t('#RecognitionHousePlanName') : t('#RecognitionApartmentPlanName');
}

export async function storeRecognized(xml: string, meta: { name: string; score: number; floors: FloorStatus[] },
  onProgress: (done: number, total: number) => void): Promise<RecognizedProject> {
  const projectId = 'local-' + crypto.randomUUID();
  const scene = await sceneFromXml(xml, projectId, onProgress);
  if (!scene.levels.some(level => level.walls.length)) throw new RecognitionError('not_recognized', 'The project has no walls');
  const onPlan = opensOnPlan(meta.score);
  try {
    writeProject({ id: projectId, name: meta.name, scene: freshUniqueIds(scene), savedAt: Date.now() });
  } catch {
    throw new RecognitionError('storage', 'The browser refused to store the project');
  }
  writeProjectView(projectId, parseProjectView({ mode: onPlan ? 'plan' : 'scene', levelIndex: 0, savedAt: Date.now() })!);
  return { projectId, name: meta.name, score: meta.score, floors: meta.floors, onPlan };
}
